/**
 * src/admin-ui/src/utils/keycloak.ts
 *
 * Native Keycloak Authorization Code + PKCE integration.
 */
import Keycloak from 'keycloak-js';

import { User } from '../types/auth';

interface TokenClaims {
  sub?: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  realm_access?: { roles?: string[] };
  resource_access?: Record<string, { roles?: string[] }>;
}

const keycloakUrl = process.env.REACT_APP_KEYCLOAK_URL || 'http://localhost:8080/keycloak';
const keycloakRealm = process.env.REACT_APP_KEYCLOAK_REALM || 'platform';
const keycloakClientId = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || 'apibar-web';
const roleClientId = process.env.REACT_APP_KEYCLOAK_ROLE_CLIENT_ID || 'apibar-api';

export const keycloak = new Keycloak({
  url: keycloakUrl,
  realm: keycloakRealm,
  clientId: keycloakClientId,
});

let initPromise: Promise<boolean> | null = null;

/**
 * Initialize the browser session exactly once.
 */
export const initKeycloak = (): Promise<boolean> => {
  if (initPromise === null) {
    initPromise = keycloak.init({
      onLoad: 'check-sso',
      silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
      checkLoginIframe: false,
      pkceMethod: 'S256',
      flow: 'standard',
    });
  }
  return initPromise;
};

/**
 * Redirect to the native Keycloak login page.
 */
export const login = async (redirectPath = '/dashboard'): Promise<void> => {
  await keycloak.login({
    redirectUri: `${window.location.origin}${redirectPath}`,
  });
};

/**
 * Redirect to the native Keycloak registration page.
 */
export const register = async (): Promise<void> => {
  await keycloak.register({
    redirectUri: `${window.location.origin}/dashboard`,
  });
};

/**
 * End the Keycloak SSO session and return to the app.
 */
export const logout = async (): Promise<void> => {
  await keycloak.logout({
    redirectUri: `${window.location.origin}/login`,
  });
};

/**
 * Return a fresh access token without persisting it in browser storage.
 */
export const getValidAccessToken = async (minValidity = 30): Promise<string | null> => {
  if (!keycloak.authenticated) {
    return null;
  }

  try {
    await keycloak.updateToken(minValidity);
    return keycloak.token || null;
  } catch {
    await logout();
    return null;
  }
};

/**
 * Extract API Bar roles from the current verified Keycloak token.
 */
export const getTokenRoles = (tokenParsed: TokenClaims | undefined): string[] => {
  if (!tokenParsed) {
    return [];
  }

  const realmRoles = tokenParsed.realm_access?.roles || [];
  const clientRoles = tokenParsed.resource_access?.[roleClientId]?.roles || [];
  return Array.from(new Set([...realmRoles, ...clientRoles])).sort();
};

/**
 * Convert Keycloak profile claims into application user state.
 */
export const getAuthenticatedUser = (): User | null => {
  const token = keycloak.tokenParsed;
  const profile = token as TokenClaims | undefined;
  if (!keycloak.authenticated || !profile?.sub) {
    return null;
  }

  return {
    id: profile.sub,
    email: profile.email || '',
    name: profile.name || profile.preferred_username || profile.email || 'User',
    roles: getTokenRoles(profile),
    provider: 'keycloak',
  };
};

export const keycloakConfig = {
  url: keycloakUrl,
  realm: keycloakRealm,
  clientId: keycloakClientId,
  roleClientId,
};
